import React, { useState, useEffect, useRef, useCallback } from "react";
import api from "@/lib/api";
import { MagnifyingGlass, CheckCircle } from "@phosphor-icons/react";

/**
 * SchoolAutocomplete — debounced search against /schools-directory/search
 * Props:
 *   value       — current school name string
 *   onChange    — (name) => void
 *   onSelect   — (match) => void  — called with full match object {school_code, name, city, state, country, edition}
 *   testid     — data-testid prefix
 */
export function SchoolAutocomplete({ value, onChange, onSelect, testid }) {
  const [query, setQuery] = useState(value || "");
  const [results, setResults] = useState([]);
  const [open, setOpen] = useState(false);
  const [activeIdx, setActiveIdx] = useState(-1);
  const [loading, setLoading] = useState(false);
  const wrapRef = useRef(null);
  const inputRef = useRef(null);
  const debounceRef = useRef(null);

  // Sync external value → local query (when parent resets)
  useEffect(() => { setQuery(value || ""); }, [value]);

  const doSearch = useCallback(async (q) => {
    if (!q || q.length < 2) { setResults([]); setOpen(false); return; }
    setLoading(true);
    try {
      const { data } = await api.get(`/schools-directory/search?q=${encodeURIComponent(q)}`);
      setResults(data || []);
      setOpen((data || []).length > 0);
      setActiveIdx(-1);
    } catch { setResults([]); }
    setLoading(false);
  }, []);

  const handleChange = (e) => {
    const v = e.target.value;
    setQuery(v);
    onChange(v);
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => doSearch(v), 300);
  };

  const selectItem = (item) => {
    setQuery(item.name);
    onChange(item.name);
    onSelect(item);
    setOpen(false);
    setResults([]);
  };

  const handleKeyDown = (e) => {
    if (!open || !results.length) return;
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActiveIdx((prev) => (prev < results.length - 1 ? prev + 1 : 0));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActiveIdx((prev) => (prev > 0 ? prev - 1 : results.length - 1));
    } else if (e.key === "Enter" && activeIdx >= 0) {
      e.preventDefault();
      selectItem(results[activeIdx]);
    } else if (e.key === "Escape") {
      setOpen(false);
    }
  };

  // Close on outside click
  useEffect(() => {
    const handler = (e) => { if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const locationLabel = (s) => {
    const parts = [s.city, s.state].filter(Boolean);
    if (s.country && s.country !== "US") parts.push(s.country);
    return parts.join(", ");
  };

  return (
    <div ref={wrapRef} className="relative">
      <label className="block">
        <span className="mb-1 block text-[11px] font-bold uppercase text-[#6b7280]">School name</span>
        <div className="relative">
          <MagnifyingGlass size={14} weight="bold" className="pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2 text-[#6b7280]" />
          <input
            ref={inputRef}
            type="text"
            value={query}
            onChange={handleChange}
            onKeyDown={handleKeyDown}
            onFocus={() => { if (results.length) setOpen(true); }}
            // A SEARCH BOX THAT IS OFFERING CHOICES, said in markup rather than
            // only in this file's key handler. That handler ignores Enter until
            // a suggestion is highlighted, so with the list open and nothing
            // picked yet the keypress reached the app-wide next-field rule and
            // moved the owner on while the suggestions were still on screen.
            // These are the right accessibility attributes for a combo box in
            // any case, and the shared rule stands aside for one. (ZEN-55,
            // adversarial review.)
            role="combobox"
            aria-expanded={open && results.length > 0}
            aria-autocomplete="list"
            data-testid={testid}
            placeholder="Start typing to search…"
            autoComplete="off"
            className="h-9 w-full rounded-md border border-[#C2C9D1] pl-8 pr-2 text-sm"
          />
          {loading && <span className="absolute right-2.5 top-1/2 -translate-y-1/2 text-[10px] text-[#6b7280]">…</span>}
        </div>
      </label>
      {open && results.length > 0 && (
        <ul
          className="absolute left-0 right-0 top-full z-50 mt-1 max-h-60 overflow-auto rounded-lg border border-[#C2C9D1] bg-white shadow-lg"
          data-testid={`${testid}-dropdown`}
          role="listbox"
        >
          {results.map((item, idx) => (
            <li
              key={item.school_code}
              role="option"
              aria-selected={idx === activeIdx}
              data-testid={`${testid}-option-${idx}`}
              className={`flex cursor-pointer items-center gap-2 px-3 py-2 text-sm ${idx === activeIdx ? "bg-[#034AA2]/10" : "hover:bg-[#F4F7FB]"}`}
              onMouseDown={(e) => { e.preventDefault(); selectItem(item); }}
              onMouseEnter={() => setActiveIdx(idx)}
            >
              <CheckCircle size={14} weight="fill" className="shrink-0 text-[#077A39]" />
              <div className="min-w-0 flex-1">
                <div className="truncate font-medium">{item.name}</div>
                <div className="truncate text-[11px] text-[#6b7280]">
                  {locationLabel(item)} · Code: {item.school_code}
                </div>
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

/**
 * SchoolEligibilityBadge — green or amber badge based on directory match
 * Props:
 *   schoolCode     — current school_529_code
 *   schoolName     — current school name
 *   directoryInfo  — from /schools-directory/info (has edition)
 */
export function SchoolEligibilityBadge({ matched, edition }) {
  if (matched) {
    return (
      <div className="mt-1.5 flex items-start gap-1.5 rounded-md border border-[#077A39]/30 bg-[#077A39]/5 px-2.5 py-1.5 text-[11px] leading-relaxed text-[#065F28]" data-testid="school-eligible-badge">
        <CheckCircle size={14} weight="fill" className="mt-0.5 shrink-0 text-[#077A39]" />
        <span>
          <b>529-eligible</b> — on the U.S. Dept. of Education Federal School Code List
          {edition ? ` (${edition})` : ""}.
        </span>
      </div>
    );
  }
  return null;
}

export function SchoolNotFoundNote() {
  return (
    <div className="mt-1.5 rounded-md border border-[#D97706]/30 bg-[#FFFBEB] px-2.5 py-1.5 text-[11px] leading-relaxed text-[#92400E]" data-testid="school-not-found-note">
      We couldn&apos;t find this school on the Federal School Code List. That doesn&apos;t always mean it&apos;s ineligible — check directly:{" "}
      <a href="https://ope.ed.gov/dapip/" target="_blank" rel="noopener noreferrer" className="font-semibold underline">DAPIP</a>
      {" "}and{" "}
      <a href="https://fsapartners.ed.gov/knowledge-center/library/resource-type/Federal%20School%20Code%20Lists" target="_blank" rel="noopener noreferrer" className="font-semibold underline">FSA School Code Lists</a>.
    </div>
  );
}
